import React, { SyntheticEvent, useContext, useState } from "react";

import { Alert, Button, CircularProgress, Grid, Snackbar } from "@mui/material";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import {
  Role,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import { updateTaxExemption } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  NO_DISPLAY_DATA,
} from "constants/index";
import { OrgDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

const UpdateTaxExemption = () => {
  const classes = mainStyles();
  const { regionCode } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);

  const organizationId = orgData?.id || "";
  const subscription = orgData?.subscription?.[0];

  const disableTaxExemptBtn = [
    SubscriptionType.FREE,
    SubscriptionType.FREE_ORG,
  ].includes(subscription?.type);

  const taxExemptData = () => {
    if (subscription?.chargify?.taxExempt === undefined) {
      return NO_DISPLAY_DATA;
    }

    const taxExemptDisplay = (subscription?.chargify?.taxExempt).toString();
    return `${taxExemptDisplay.charAt(0).toUpperCase()}${taxExemptDisplay.slice(
      1,
    )}`;
  };

  const taxExemptButtonDisplay = (!subscription?.chargify
    ?.taxExempt).toString();

  const handleUpdateTaxExemptionState = (newTaxExempt: boolean) => {
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription && newOrgData.subscription[0].chargify) {
      newOrgData.subscription[0].chargify.taxExempt = newTaxExempt;
      setOrgData(newOrgData);
    }
  };

  const handleUpdateTaxExemptionReq = () => {
    setShowLoadingSpinner(true);
    const targetTaxExemptionStatus = !subscription?.chargify?.taxExempt;
    updateTaxExemption(
      {
        organizationId: toUUID(organizationId),
        isTaxExempt: targetTaxExemptionStatus,
      },
      callOptionsHeaders(regionCode, organizationId),
    )
      .then(() => {
        handleUpdateTaxExemptionState(targetTaxExemptionStatus);
        setOpenSuccessAlert(true);
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  const handleClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined,
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  return (
    <>
      <SubscriptionAction title="Tax Exempt:" displayInfo={taxExemptData()}>
        {showLoadingSpinner ? (
          <Grid
            container
            justifyContent="center"
            className={clsx(classes.spaceTopSmall, classes.spaceRightMedium)}
          >
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            disabled={disableTaxExemptBtn}
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="change-tax-exempt-btn"
          >
            SET TO {taxExemptButtonDisplay}
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Confirm Action"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => handleUpdateTaxExemptionReq()}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        Are you sure you want to set Tax Exempt to{" "}
        {taxExemptButtonDisplay.charAt(0).toUpperCase() +
          taxExemptButtonDisplay.slice(1)}
        ?
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
      >
        <Alert severity="success" variant="filled">
          Tax exemption status updated
        </Alert>
      </Snackbar>
    </>
  );
};

export default UpdateTaxExemption;
